import React, { Children, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { randomId } from '@mui/x-data-grid-generator';

import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Checkbox from '@mui/material/Checkbox';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import LoadingButton from '@mui/lab/LoadingButton';
import logoDigiForma from 'imgs/logo-digiforma.svg';
import DigiformaStaffingBadge from 'components/object/DigiformaStaffingBadge';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

import TextFieldCustom from 'components/object/formElement/TextFieldCustom';

import { formatDate } from 'components/object/Utils';
import { ContextTraining } from 'context/ContextTraining';
import { ContextTrainingSearch } from 'context/ContextTrainingSearch';


const TrainingStaffingDetails = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);

  //context
  const {
    training,
    getTraining,
    saveSessionStaffing,
    validateTrainingStaffing, cancelTrainingStaffing,
    trainingSessionStaffingRows, setTrainingSessionStaffingRows
  } = useContext(ContextTraining);

  const {
    trainingSearch, setTrainingSearch,
    saveTrainingSearchInternalNote
  } = useContext(ContextTrainingSearch);

  //order mecanic
  const [orderBy, setOrderBy] = useState([]);
  const [hoveredColumn, setHoveredColumn] = useState(null);


  const toggleOrderByColumn = (column, sessionId) => {
    setOrderBy((prev) => {
      const fullKey = `${column}-${sessionId}`;
      const existing = prev.find((o) => o.column === fullKey);
      if (!existing) {
        return [...prev, { column: fullKey, direction: 'asc' }];
      } else if (existing.direction === 'asc') {
        return prev.map((o) =>
          o.column === fullKey ? { ...o, direction: 'desc' } : o
        );
      } else {
        return prev.filter((o) => o.column !== fullKey);
      }
    });
  };

  const getOrderDirection = (column, sessionId) => {
    const fullKey = `${column}-${sessionId}`;
    return orderBy.find((o) => o.column === fullKey)?.direction ?? null;
  };

  const sortRows = (rows, sessionId) => {
    const localOrderBy = orderBy
      .filter((order) => order.column.endsWith(`-${sessionId}`))
      .map(({ column, direction }) => ({
        key: column.replace(`-${sessionId}`, ''),
        direction,
      }));
      
    if (localOrderBy.length === 0) {
      // ✅ order by id by default
      return rows.slice().sort((a, b) => (a.id > b.id ? 1 : -1));
    }

    return rows.slice().sort((a, b) => {
      for (let { key, direction } of localOrderBy) {
        const aVal = a[key];
        const bVal = b[key];

        //empty field management
        const isEmpty = (val) =>
          val === null || val === undefined || (typeof val === 'string' && val.trim() === '');

        const aEmpty = isEmpty(aVal);
        const bEmpty = isEmpty(bVal);

        if (aEmpty && !bEmpty) return direction === 'asc' ? -1 : 1;
        if (!aEmpty && bEmpty) return direction === 'asc' ? 1 : -1;
        if (aEmpty && bEmpty) continue;

        //order
        if (aVal > bVal) return direction === 'asc' ? 1 : -1;
        if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      }
      //order by id by default
      return a.id > b.id ? 1 : -1;
    });
  };
  

  const handleModifySearchClick = () => {
    navigate(`/formation/recherche?id=${training.id}&key=${randomId()}`);
  }

  const handleValidationClick = async () => {
    //first save checked rows
    await handleSaveClick();
    //then validate staffing
    setLoading(true);
    await validateTrainingStaffing();
    setLoading(false);
  }

  const handleCancelationClick = async () => {
    setLoading(true);
    //cancel staffing
    await cancelTrainingStaffing();
    setLoading(false);
  }

  const handleSaveClick = async() => {
    setLoading(true);
    //save staffing — wait for every session's save to actually complete before returning,
    //otherwise a subsequent validation call can run against still in-flight/incomplete data.
    await Promise.all(trainingSessionStaffingRows.map((session) => {
      var checkedRows = [];
      session.rows.map((row) => {
        if(row.selected){
          checkedRows.push(row);
        }
      })
      return saveSessionStaffing(checkedRows, session.sessionId, session.digiformaSessionId);
    }));
    //save internal note
    await saveTrainingSearchInternalNote();
    // Refresh training.subsessions[].staffings from the DB — saveSessionStaffing diffs
    // against that state to decide what to delete, so without this refresh a second
    // check/uncheck-and-save cycle (without a page reload in between) can't see what the
    // first save just persisted, and silently fails to delete an unchecked row.
    await getTraining();
    setLoading(false);
  }

  const formatName = (firstname, lastname) => {
    var retour = '';
    if (firstname) {
      retour += firstname;
    }
    if (lastname) {
      if (retour != '') {
        retour += ' ';
      }
      retour += lastname;
    }
    return retour;
  }

  const changeResultRowCheckHandler = (event) => {
    var result = trainingSessionStaffingRows.map((block) => {
      block.rows = block.rows.map((row) => {
        if(row.rowId == event.target.value){
          row.selected = !row.selected;
        }
        return row;
      });
      return block;
    });
    setTrainingSessionStaffingRows(result);
  }

  const changeInternalNoteHandler = (event) => {
    setTrainingSearch({ ...trainingSearch, internal_note: event.target.value });
  }

  const sortedSessionStaffingRows = [...trainingSessionStaffingRows].sort(
    (a, b) => new Date(a.startDate) - new Date(b.startDate)
  );
  return (
    <>
      <Accordion className="details-block training-search-commentary-block" disableGutters sx={{
        '&::before': {
          display: 'none',
        }
      }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          Commentaire
        </AccordionSummary>
        <AccordionDetails>
          <TextFieldCustom
            label="Note Interne"
            multiline
            value={trainingSearch?.internal_note}
            onBlur={changeInternalNoteHandler}
            rows={3}
            fullWidth
            disabled={!training.isWritable}
          />
        </AccordionDetails>
      </Accordion>

      <div className="TrainingStaffingSearchResult-block">
        <div className="details-block training-search-result-block">
          {sortedSessionStaffingRows.length > 0 ? Children.toArray(sortedSessionStaffingRows.map((block, index) => {
            let title = block.thematic;
            title += ` (du ${formatDate(block.startDate, false)} au ${formatDate(block.endDate, false)})`;

            return (
            <div>
              <div className='training-search-result-row-header'>
                  {title} - {block.n_days} jour{block.n_days > 1 ? 's' : ''}
                  {' '}<DigiformaStaffingBadge modules={[{ name: block.thematic || 'Module', digiformaStaffing: block.digiformaStaffing }]} />
              </div>
              
              {block.rows?.length > 0 ? 
                <div className="table">
                  <div className="table-row th">
                    {training.isWritable && <div className="table-cell check"></div>}
                    <div className="table-cell date">Du</div>
                    <div className="table-cell date">Au</div>
                    <div className="table-cell intervenant">Intervenant</div>
                    <div
                      className="table-cell entreprise"
                      onClick={() => toggleOrderByColumn('company', block.digiformaSessionId)}
                      onMouseOver={() => setHoveredColumn('company')}
                      onMouseOut={() => setHoveredColumn(null)}
                    >
                      Entreprise
                      {(hoveredColumn === 'company' || getOrderDirection('company', block.digiformaSessionId)) && (
                        <span className="orderPicto">
                          {getOrderDirection('company', block.digiformaSessionId) === 'asc' && <ArrowDropDownIcon />}
                          {getOrderDirection('company', block.digiformaSessionId) === 'desc' && <ArrowDropUpIcon />}
                          {!getOrderDirection('company', block.digiformaSessionId) && <ArrowDropDownIcon style={{ opacity: 0.3 }} />}
                        </span>
                      )}
                    </div>

                    <div
                      className="table-cell lieu"
                      onClick={() => toggleOrderByColumn('city', block.digiformaSessionId)}
                      onMouseOver={() => setHoveredColumn('city')}
                      onMouseOut={() => setHoveredColumn(null)}
                    >
                      Lieu
                      {(hoveredColumn === 'city' || getOrderDirection('city', block.digiformaSessionId)) && (
                        <span className="orderPicto">
                          {getOrderDirection('city', block.digiformaSessionId) === 'asc' && <ArrowDropDownIcon />}
                          {getOrderDirection('city', block.digiformaSessionId) === 'desc' && <ArrowDropUpIcon />}
                          {!getOrderDirection('city', block.digiformaSessionId) && <ArrowDropDownIcon style={{ opacity: 0.3 }} />}
                        </span>
                      )}
                    </div>
                    <div className="table-cell email">Email</div>
                    <div className="table-cell telephone">Téléphone</div>
                    <div className="table-cell digiformaLink"></div>
                  </div>

                  {Children.toArray(sortRows(block.rows, block.digiformaSessionId).map((row, index) => (
                    <div className="table-row">
                      {training.isWritable && <div className="table-cell check">
                        <Checkbox
                          checked={row?.selected ? true : false}
                          onChange={changeResultRowCheckHandler}
                          name="checkResultRow[]"
                          value={row.rowId}
                          disabled={!training.isWritable}
                        />
                      </div>}
                      <div className="table-cell">{formatDate(row.startDate, false)}</div>
                      <div className="table-cell">{formatDate(row.endDate, false)}</div>
                      <div className="table-cell">{formatName(row.firstname, row.lastname)}</div>
                      <div className="table-cell">{row.company}</div>
                      <div className="table-cell">{row.cityCode ? row.cityCode + " " : ""}{row.city}</div>
                      <div className="table-cell email"><Tooltip title={row.email}><div>{row.email}</div></Tooltip></div>
                      <div className="table-cell">{row.phone}</div>
                      <div className="table-cell">
                        <div className="linkButton">
                          <a href={`https://app.digiforma.com/instructors/${row.id}/edit`} target="_blank">
                            <div className="buttonIcon">
                              <img src={logoDigiForma} alt="DigiForma" />
                            </div>
                          </a>
                        </div>
                      </div>
                    </div>
                  )))}
                </div> : 
                <div>
                  {training.isWritable ? <i>Aucun résultat</i> : <i>Aucun formateur sélectionné</i>}
                </div>
              }
            </div>
            );
          })) : (training.isWritable ? <div className="no-data">Recherche en cours...</div> : <div className="no-data">Chargement en cours...</div>)}

            <div className="block-button">
              <Stack direction="row">
                <div>
                  <Stack spacing={2} direction="row">
                    {training.isWritable ?
                      <>
                        <Button variant="contained" color="primary" onClick={handleModifySearchClick}>Modifier la recherche</Button>
                        <LoadingButton variant="contained" color="primary" loading={loading} onClick={async () => await handleSaveClick()} loadingPosition="start" startIcon={<SaveIcon />}>
                          Enregistrer
                        </LoadingButton>
                        <LoadingButton variant="contained" color="success" loading={loading} onClick={async () => await handleValidationClick()} loadingPosition="start" startIcon={<SaveIcon />}>
                          Valider le staffing
                        </LoadingButton>
                      </>
                      : 
                      <LoadingButton variant="contained" color="error" loading={loading} onClick={async () => await handleCancelationClick()} loadingPosition="start" startIcon={<CloseIcon />}>
                        Annuler le staffing
                      </LoadingButton>
                    }
                  </Stack>
                </div>
              </Stack>
            </div>
        </div>
      </div>
    </>
  );
}

export default TrainingStaffingDetails;